import { Tab } from "@headlessui/react";
import { useAtom } from "jotai";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { RateFormProps } from "@/components/Checkout/Steps/RateSelection/RateSelection";
import { Button } from "@/components/Interface/Button/Button";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { StepFlowNavigation } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowNavigation";
import { LocalLoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { RateCardGrid } from "@/components/Layout/Rates/RateCard/RateCardGrid";
import { RateTariffHint } from "@/components/Layout/Rates/RateCard/RateTariffHint";
import {
  getDataOnlyProducts,
  getMobileAbos,
  getSortedMobileAbosMonthly,
} from "@/components/Layout/Rates/rateSelection.utils";
import { RateTypeTabs } from "@/components/Layout/Rates/RateTypeTabs/RateTypeTabs";
import { transformOptionsToPreselection } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionChange/transformOptionsToPreselection";
import { useCustomerTribe } from "@/hooks/useCustomerTribe";
import { useShowFlatMobileFirstFromQueryParam } from "@/hooks/useShowFlatMobileFirstFromQueryParam";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  getAvailableSunriseRoamingPackages,
  getSim,
  useGetCustomer,
  useGetDetailTribe,
} from "@/services/api";
import {
  GetAvailableSunriseRoamingPackagesPackageType,
  SimCardDetail,
} from "@/services/model";
import {
  accessTokenAtom,
  keepTypedAlias,
  portalTariffProductIDAtom,
  tariffCheckoutPaymentPersistenceAtom,
} from "@/utils/atoms";
import { canOnlyBookData } from "@/utils/customerUtils";
import { isInactive } from "@/utils/deviceUtils";
import {
  Duration,
  findDurationPeer,
  getRateCardProductId,
  isAnyFlatMobileTariff,
  TransformedProducts,
  transformProducts,
} from "@/utils/tariffUtils";

/**
 * Step 1 of the subscription-change flow: rate selection.
 * Fetches available products for the sim and lets the user pick one.
 */
export const SubscriptionRateSelection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [simId] = useSimIdFromQueryParam();
  const [showFlatMobileFirst] = useShowFlatMobileFirstFromQueryParam();
  const { data: customerData } = useGetCustomer();
  const [accessToken] = useAtom(accessTokenAtom);
  const { data: customerTribe } = useCustomerTribe();
  const { data: tribeData } = useGetDetailTribe(customerTribe?.tribeId ?? "", {
    query: { enabled: !!accessToken && !!customerTribe?.tribeId },
  });
  const tribeDiscount = tribeData?.tribeDiscountNext;

  const [simDetail, setSimDetail] = useState<SimCardDetail | undefined>();
  const [options, setOptions] = useState<TransformedProducts | undefined>();
  const [rateTypeTabIndex, setRateTypeTabIndex] = useState(0);

  const [portalTariffProductID, setPortalTariffProductID] = useAtom(
    portalTariffProductIDAtom,
  );
  const [, setTariffPaymentPersistence] = useAtom(
    tariffCheckoutPaymentPersistenceAtom,
  );

  const canShowSwitchToMobile = !customerData || !canOnlyBookData(customerData);

  const rateFormMethods = useForm<Omit<RateFormProps, "numberPorting">>({
    defaultValues: async () => {
      const sim = await getSim({ simId: parseInt(simId) });
      const packages = await getAvailableSunriseRoamingPackages(
        parseInt(simId),
        {
          packageType: GetAvailableSunriseRoamingPackagesPackageType.Data_CH,
        },
      ).then((res) => transformProducts(res?.subscriberPackageData));

      setSimDetail(sim);
      setOptions(packages);

      const { rate: preselectedRate } = transformOptionsToPreselection(
        sim,
        packages,
        portalTariffProductID || sim?.serviceID,
      );
      const rate = preselectedRate
        ? getRateCardProductId(
            Number(preselectedRate),
            packages.processedProductsById,
          )?.toString()
        : undefined;

      const shouldStartOnMobileTab =
        portalTariffProductID && rate
          ? isAnyFlatMobileTariff(Number(rate))
          : isInactive(sim) ||
            isAnyFlatMobileTariff(sim?.serviceID) ||
            (!!sim?.localFlatSpeed &&
              sim.localFlatSpeed.includes("Flat Mobile")) ||
            showFlatMobileFirst === "true";

      setRateTypeTabIndex(shouldStartOnMobileTab ? 0 : 1);

      return { rate: rate || "" };
    },
  });

  const selectedOption = Number(rateFormMethods.watch("rate"));

  const followUpId =
    !isInactive(simDetail) && simDetail?.autoRenew
      ? simDetail.successorForRenew?.id
      : undefined;
  const isFollowUpSelected = !!followUpId && selectedOption === followUpId;
  const selectedProduct = options?.processedProductsById[selectedOption];
  // Re-booking the follow-up is pointless, but its other billing period is not.
  const followUpPeer = isFollowUpSelected
    ? findDurationPeer(
        selectedProduct,
        options?.processedProductsById ?? {},
        selectedProduct?.duration === Duration.year
          ? Duration.month
          : Duration.year,
      )
    : undefined;

  const navigateForwards = () => {
    setPortalTariffProductID(followUpPeer?.id ?? selectedOption);
    setTariffPaymentPersistence(keepTypedAlias);
    navigate(
      `${portalRoutes.manageDevice.subscription.checkout}?simId=${simDetail?.simId}`,
    );
  };

  const navigateBack = () => {
    setPortalTariffProductID(undefined);
    navigate(
      `${portalRoutes.manageDevice.subscription.display}?simId=${simDetail?.simId}`,
      { replace: true },
    );
  };

  const disableSuccessButton =
    !selectedOption || (isFollowUpSelected && !followUpPeer);

  const mobileAboProducts = getMobileAbos(options?.processedProductsById);
  const monthlyMobileAbos = getSortedMobileAbosMonthly(mobileAboProducts);

  const dataOnlyProducts = useMemo(
    () => getDataOnlyProducts(options?.processedProductsById),
    [options?.processedProductsById],
  );

  // The tariff currently booked on this SIM (not the follow-up) — shown with "Current" chip.
  const currentDataRateId = useMemo(
    () =>
      simDetail && options && !isInactive(simDetail)
        ? getRateCardProductId(
            simDetail.serviceID,
            options.processedProductsById,
          )
        : undefined,
    [simDetail, options],
  );

  const currentTariffLabel = t("portal:subscription-change.currentTariffLabel");

  const isLoading = rateFormMethods.formState.isLoading;

  return (
    <div className="flex w-full flex-col gap-6">
      <FormProvider {...rateFormMethods}>
        <CardContainer
          className="flex w-full flex-col rounded-lg pb-2"
          variant="white"
        >
          {isLoading || !options ? (
            <div className="flex justify-center py-8">
              <LocalLoadingSpinner />
            </div>
          ) : (
            <RateTypeTabs
              selectedIndex={rateTypeTabIndex}
              onIndexChange={setRateTypeTabIndex}
              listClassName="px-4 pb-2 pt-4"
              hideSecondTab={!canShowSwitchToMobile}
              firstLabel={t(
                "Onboarding.sections.rate-selection.tabs.mobileAbos",
              )}
              secondLabel={t(
                "Onboarding.sections.rate-selection.tabs.dataOnly",
              )}
            >
              <Tab.Panels className="flex flex-col gap-6 px-4 py-2">
                <Tab.Panel>
                  <RateCardGrid
                    ariaLabel={t(
                      "Onboarding.sections.rate-selection.tabs.mobileAbos",
                    )}
                    value={rateFormMethods.watch("rate")}
                    products={monthlyMobileAbos}
                    hint={<RateTariffHint />}
                    currentProductId={currentDataRateId}
                    currentLabel={currentTariffLabel}
                    tribeDiscount={tribeDiscount}
                    onChange={(rate) => rateFormMethods.setValue("rate", rate)}
                  />
                </Tab.Panel>
                <Tab.Panel>
                  <RateCardGrid
                    ariaLabel={t(
                      "Onboarding.sections.rate-selection.tabs.dataOnly",
                    )}
                    value={rateFormMethods.watch("rate")}
                    products={dataOnlyProducts}
                    hint={<RateTariffHint />}
                    currentProductId={currentDataRateId}
                    currentLabel={currentTariffLabel}
                    onChange={(rate) => rateFormMethods.setValue("rate", rate)}
                  />
                </Tab.Panel>
              </Tab.Panels>
            </RateTypeTabs>
          )}
        </CardContainer>
      </FormProvider>

      <StepFlowNavigation
        primaryButton={
          <Button
            dataTestid="success-button"
            variant="primary"
            disabled={disableSuccessButton}
            loading={isLoading}
            onClick={navigateForwards}
          >
            {t("common.buttons.continue")}
          </Button>
        }
        secondaryButton={
          <Button variant="secondary" onClick={navigateBack}>
            {t("common.buttons.cancel")}
          </Button>
        }
      />
    </div>
  );
};
